import * as THREE from 'three';
import { World } from './world';
import { Player } from './player';
import { Interaction } from './interaction';
import { NPCManager } from './npcs';
import { HUD } from './hud';
import { SoundManager } from './audio';
import { DayNightCycle } from './daynight';
import { Inventory } from './inventory';
import { NetworkClient } from './client/network';
import { RemotePlayers } from './client/remote-players';
import { CharacterPreview } from './client/character-preview';
import { BLOCKS } from './blocks';
import { escapeHtml } from './sanitize';
import {
  type PlayerAppearance,
  HAIR_PRESETS, SKIN_PRESETS, SHIRT_PRESETS, PANTS_PRESETS, SHOE_PRESETS,
  MODEL_TYPES,
  defaultAppearance,
} from './protocol';
import { isMobile } from './client/mobile-detect';
import { TouchControls } from './client/touch-controls';
import { showCursor, hideCursor } from './client/cursor-manager';
import { initI18n, t, getModelLabel } from './i18n';

initI18n();

// --- Mobile detection ---
const mobile = isMobile();
if (mobile) {
  document.body.classList.add('is-mobile');
  // Try to lock to landscape
  try {
    (screen.orientation as any)?.lock?.('landscape').catch(() => {});
  } catch {}
}

// Cursor visible on initial menu
showCursor('menu');

// --- Renderer ---
const renderer = new THREE.WebGLRenderer({ antialias: !mobile });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, mobile ? 2 : 4));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
document.body.appendChild(renderer.domElement);
renderer.domElement.style.visibility = 'hidden';

// --- Camera ---
const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 200);

// --- Scene ---
const scene = new THREE.Scene();
const SKY_COLOR = 0x87ceeb;
scene.background = new THREE.Color(SKY_COLOR);
scene.fog = new THREE.Fog(SKY_COLOR, 50, 120);

// --- Lighting ---
const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
dirLight.position.set(50, 80, 30);
dirLight.castShadow = true;
dirLight.shadow.mapSize.width = 1024;
dirLight.shadow.mapSize.height = 1024;
dirLight.shadow.camera.near = 0.5;
dirLight.shadow.camera.far = 200;
dirLight.shadow.camera.left = -60;
dirLight.shadow.camera.right = 60;
dirLight.shadow.camera.top = 60;
dirLight.shadow.camera.bottom = -60;
dirLight.shadow.bias = -0.001;
scene.add(dirLight);

const ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
scene.add(ambientLight);

const hemiLight = new THREE.HemisphereLight(0x87ceeb, 0x5b8731, 0);
scene.add(hemiLight);

// --- Held-item overlay (first-person hand) ---
const handScene = new THREE.Scene();
const handCamera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.01, 10);
handCamera.position.set(0, 0, 0);
handScene.add(new THREE.AmbientLight(0xffffff, 0.7));
const handDirLight = new THREE.DirectionalLight(0xffffff, 1.2);
handDirLight.position.set(1, 2, 1);
handScene.add(handDirLight);

let handMesh: THREE.Object3D | null = null;
let handBlockId = -1;
let handSwing = 0; // animation for block placement/breaking
let torchLight: THREE.PointLight | null = null;

function updateHandMesh(blockId: number) {
  if (blockId === handBlockId) return;
  handBlockId = blockId;

  if (handMesh) {
    handScene.remove(handMesh);
    handMesh = null;
  }

  if (blockId <= 0) return;

  const block = BLOCKS[blockId];
  if (!block) return;

  if (block.isItem) {
    // Items: render as a flat held stick/ingot
    if (blockId === 19 || blockId === 26 || blockId === 27) {
      // Stick or pickaxe — long thin shape
      const group = new THREE.Group();
      const handleGeo = new THREE.BoxGeometry(0.06, 0.4, 0.06);
      const handleMat = new THREE.MeshLambertMaterial({ color: 0x8b6c3a });
      const handle = new THREE.Mesh(handleGeo, handleMat);
      group.add(handle);
      if (blockId === 26 || blockId === 27) {
        // Pickaxe head
        const headColor = blockId === 26 ? 0xbc9458 : 0xddccbb;
        const headGeo = new THREE.BoxGeometry(0.2, 0.06, 0.06);
        const head = new THREE.Mesh(headGeo, new THREE.MeshLambertMaterial({ color: headColor }));
        head.position.y = 0.23;
        group.add(head);
      }
      group.position.set(0.35, -0.35, -0.5);
      group.rotation.set(0, 0, -0.5);
      handMesh = group;
    } else {
      // Generic item — small cube
      const geo = new THREE.BoxGeometry(0.15, 0.15, 0.15);
      const mat = new THREE.MeshLambertMaterial({ color: block.color });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(0.35, -0.35, -0.5);
      handMesh = mesh;
    }
  } else if (blockId === 20) {
    // Torch — stick with flame
    const group = new THREE.Group();
    const stickGeo = new THREE.BoxGeometry(0.05, 0.35, 0.05);
    const stick = new THREE.Mesh(stickGeo, new THREE.MeshLambertMaterial({ color: 0x8b6c3a }));
    group.add(stick);
    const flameGeo = new THREE.BoxGeometry(0.08, 0.1, 0.08);
    const flame = new THREE.Mesh(flameGeo, new THREE.MeshLambertMaterial({ color: 0xffcc00, emissive: 0xff8800, emissiveIntensity: 0.8 }));
    flame.position.y = 0.22;
    group.add(flame);
    group.position.set(0.35, -0.3, -0.5);
    group.rotation.set(-0.3, 0.2, -0.3);
    handMesh = group;
  } else {
    // Regular block — small cube
    const geo = new THREE.BoxGeometry(0.25, 0.25, 0.25);
    const topColor = block.topColor || block.color;
    const sideColor = block.color;
    const bottomColor = block.bottomColor || block.color;
    const materials = [
      new THREE.MeshLambertMaterial({ color: sideColor }),
      new THREE.MeshLambertMaterial({ color: sideColor }),
      new THREE.MeshLambertMaterial({ color: topColor }),
      new THREE.MeshLambertMaterial({ color: bottomColor }),
      new THREE.MeshLambertMaterial({ color: sideColor }),
      new THREE.MeshLambertMaterial({ color: sideColor }),
    ];
    const mesh = new THREE.Mesh(geo, materials);
    mesh.position.set(0.35, -0.35, -0.5);
    mesh.rotation.set(0.4, 0.8, 0);
    handMesh = mesh;
  }

  if (handMesh) handScene.add(handMesh);

  // Manage held torch light in main scene
  if (blockId === 20) {
    if (!torchLight) {
      torchLight = new THREE.PointLight(0xffaa44, 2.0, 20, 1);
      scene.add(torchLight);
    }
  } else if (torchLight) {
    scene.remove(torchLight);
    torchLight = null;
  }
}

// --- Shared modules ---
let inventory = new Inventory();
let hud = new HUD(inventory);
const sound = new SoundManager();

// --- Game state ---
let world: World;
let player: Player;
let interaction: Interaction;
let npcManager: NPCManager;
let dayNight: DayNightCycle;
let remotePlayers: RemotePlayers;
let network: NetworkClient;
let spawnX = 8;
let spawnY = 220;
let spawnZ = 8;
let gameRunning = false;
let myPlayerId = -1;
let myPlayerName = '';
let touchControls: TouchControls | null = null;

// --- Music block note state (client-side, position → note index) ---
const musicNoteState = new Map<string, number>();

// --- TNT Fuse countdown display ---
interface TNTFuse {
  wx: number; wy: number; wz: number;
  timer: number;
  duration: number;
  meshes: THREE.Mesh[];
  lastText: string;
}
const tntFuses: TNTFuse[] = [];

function createCountdownTexture(text: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, 128, 128);
  // Red semi-transparent background
  ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
  ctx.fillRect(0, 0, 128, 128);
  // Bold white text
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 80px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 64, 64);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  return tex;
}

function startTNTFuse(wx: number, wy: number, wz: number, duration: number) {
  // Remove any existing fuse at this position
  removeTNTFuse(wx, wy, wz);

  const meshes: THREE.Mesh[] = [];
  const cx = wx + 0.5, cy = wy + 0.5, cz = wz + 0.5;
  const offset = 0.505; // Slightly outside the block face

  // 6 face directions: +X, -X, +Y, -Y, +Z, -Z
  const faces = [
    { pos: [cx + offset, cy, cz], rot: [0, Math.PI / 2, 0] },
    { pos: [cx - offset, cy, cz], rot: [0, -Math.PI / 2, 0] },
    { pos: [cx, cy + offset, cz], rot: [-Math.PI / 2, 0, 0] },
    { pos: [cx, cy - offset, cz], rot: [Math.PI / 2, 0, 0] },
    { pos: [cx, cy, cz + offset], rot: [0, 0, 0] },
    { pos: [cx, cy, cz - offset], rot: [0, Math.PI, 0] },
  ];

  const tex = createCountdownTexture(Math.ceil(duration).toString());
  for (const face of faces) {
    const mat = new THREE.MeshBasicMaterial({ map: tex.clone(), transparent: true, depthWrite: false, side: THREE.DoubleSide });
    const geo = new THREE.PlaneGeometry(0.9, 0.9);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(face.pos[0], face.pos[1], face.pos[2]);
    mesh.rotation.set(face.rot[0], face.rot[1], face.rot[2]);
    scene.add(mesh);
    meshes.push(mesh);
  }

  tntFuses.push({ wx, wy, wz, timer: duration, duration, meshes, lastText: Math.ceil(duration).toString() });
}

function removeTNTFuse(wx: number, wy: number, wz: number) {
  for (let i = tntFuses.length - 1; i >= 0; i--) {
    const f = tntFuses[i];
    if (f.wx === wx && f.wy === wy && f.wz === wz) {
      for (const m of f.meshes) {
        scene.remove(m);
        m.geometry.dispose();
        const mat = m.material as THREE.MeshBasicMaterial;
        mat.map?.dispose();
        mat.dispose();
      }
      tntFuses.splice(i, 1);
    }
  }
}

function updateTNTFuses(dt: number) {
  for (let i = tntFuses.length - 1; i >= 0; i--) {
    const f = tntFuses[i];
    f.timer -= dt;
    if (f.timer <= 0) {
      // Fuse done — clean up (explosion handled by TNTExplode message)
      for (const m of f.meshes) {
        scene.remove(m);
        m.geometry.dispose();
        const mat = m.material as THREE.MeshBasicMaterial;
        mat.map?.dispose();
        mat.dispose();
      }
      tntFuses.splice(i, 1);
      continue;
    }

    // Update countdown text
    const newText = Math.ceil(f.timer).toString();
    const pulse = 1 + 0.1 * Math.sin(f.timer * 10); // Pulsing effect
    if (f.lastText !== newText) {
      f.lastText = newText;
      for (const m of f.meshes) {
        const mat = m.material as THREE.MeshBasicMaterial;
        mat.map?.dispose();
        mat.map = createCountdownTexture(newText);
        mat.needsUpdate = true;
      }
    }
    for (const m of f.meshes) {
      m.scale.setScalar(pulse);
    }
  }
}

// --- Camera shake ---
let cameraShakeTimer = 0;

// --- Footprint system (paint trail) ---
interface Footprint {
  mesh: THREE.Mesh;
  timer: number;
}
const footprints: Footprint[] = [];
let footprintTimer = 0;

function addFootprint(x: number, y: number, z: number, paintBlockId: number) {
  const colors: Record<number, number> = { 51: 0xff3333, 52: 0x3333ff, 53: 0x33ff33, 54: 0xffff33 };
  const color = colors[paintBlockId] || 0xffffff;
  const geo = new THREE.PlaneGeometry(0.3, 0.3);
  const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(x, y + 0.01, z);
  scene.add(mesh);
  footprints.push({ mesh, timer: 10 });
}

function updateFootprints(dt: number) {
  for (let i = footprints.length - 1; i >= 0; i--) {
    footprints[i].timer -= dt;
    if (footprints[i].timer <= 0) {
      scene.remove(footprints[i].mesh);
      footprints[i].mesh.geometry.dispose();
      (footprints[i].mesh.material as THREE.MeshBasicMaterial).dispose();
      footprints.splice(i, 1);
    } else if (footprints[i].timer < 2) {
      // Fade out
      (footprints[i].mesh.material as THREE.MeshBasicMaterial).opacity = footprints[i].timer / 2 * 0.6;
    }
  }
}

// --- PointLight manager (dynamic lighting mode) ---
const MAX_POINT_LIGHTS = 64;
const LIGHT_REFRESH_INTERVAL = 30; // frames between full refresh
let lightingMode: 'lightmap' | 'pointlight' = (() => {
  const saved = localStorage.getItem('jannecraft-lighting');
  return saved === 'pointlight' ? 'pointlight' : 'lightmap';
})();
let lightRefreshCounter = 0;

const pointLightManager = {
  lights: new Map<string, THREE.PointLight>(),

  refresh(playerPos: THREE.Vector3) {
    if (!world || lightingMode !== 'pointlight') return;

    const sources = world.getLightSources(playerPos.x, playerPos.z, 96);

    // Sort by distance to player, nearest first
    sources.sort((a, b) => {
      const da = (a.x - playerPos.x) ** 2 + (a.y - playerPos.y) ** 2 + (a.z - playerPos.z) ** 2;
      const db = (b.x - playerPos.x) ** 2 + (b.y - playerPos.y) ** 2 + (b.z - playerPos.z) ** 2;
      return da - db;
    });

    // Keep nearest MAX_POINT_LIGHTS
    const activeKeys = new Set<string>();
    const count = Math.min(sources.length, MAX_POINT_LIGHTS);

    for (let i = 0; i < count; i++) {
      const s = sources[i];
      const key = `${s.x},${s.y},${s.z}`;
      activeKeys.add(key);

      if (!this.lights.has(key)) {
        this.addLight(s.x, s.y, s.z, s.level);
      }
    }

    // Remove lights no longer in the active set
    for (const [key, light] of this.lights) {
      if (!activeKeys.has(key)) {
        scene.remove(light);
        light.dispose();
        this.lights.delete(key);
      }
    }
  },

  addLight(x: number, y: number, z: number, level: number) {
    const key = `${x},${y},${z}`;
    if (this.lights.has(key)) return;

    const intensity = (level / 15) * 2.5;
    const distance = level + 2;
    const light = new THREE.PointLight(0xffaa44, intensity, distance, 1.5);
    light.position.set(x + 0.5, y + 0.5, z + 0.5);
    scene.add(light);
    this.lights.set(key, light);
  },

  removeLight(key: string) {
    const light = this.lights.get(key);
    if (light) {
      scene.remove(light);
      light.dispose();
      this.lights.delete(key);
    }
  },

  clear() {
    for (const [, light] of this.lights) {
      scene.remove(light);
      light.dispose();
    }
    this.lights.clear();
  },
};

function applyLightingMode() {
  if (dayNight) {
    dayNight.pointLightMode = lightingMode === 'pointlight';
  }
  if (lightingMode === 'pointlight') {
    if (player) {
      pointLightManager.refresh(player.position);
    }
  } else {
    pointLightManager.clear();
  }
  localStorage.setItem('jannecraft-lighting', lightingMode);
}

// --- Particle effects ---
interface Particle {
  mesh: THREE.Mesh;
  vx: number; vy: number; vz: number;
  timer: number;
  maxTime: number;
}
const particles: Particle[] = [];

function spawnFireworkEffect(x: number, y: number, z: number) {
  // Trail particles going up
  const trailColor = 0xffaa00;
  const trailGeo = new THREE.SphereGeometry(0.06, 4, 4);
  for (let i = 0; i < 8; i++) {
    const mat = new THREE.MeshBasicMaterial({ color: trailColor, transparent: true, opacity: 0.8 });
    const mesh = new THREE.Mesh(trailGeo, mat);
    mesh.position.set(x, y, z);
    scene.add(mesh);
    particles.push({
      mesh, timer: 1.5 + i * 0.1, maxTime: 1.5 + i * 0.1,
      vx: (Math.random() - 0.5) * 0.5, vy: 8 + Math.random() * 2, vz: (Math.random() - 0.5) * 0.5
    });
  }

  // Delayed explosion burst
  setTimeout(() => {
    sound.playFireworkExplode();
    const burstY = y + 12;
    const burstColors = [0xff3333, 0x33ff33, 0x3333ff, 0xffff33, 0xff33ff, 0x33ffff, 0xff8800, 0xffffff];
    const sphereGeo = new THREE.SphereGeometry(0.1, 4, 4);
    for (let i = 0; i < 25; i++) {
      const color = burstColors[Math.floor(Math.random() * burstColors.length)];
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
      const mesh = new THREE.Mesh(sphereGeo, mat);
      mesh.position.set(x, burstY, z);
      scene.add(mesh);
      const angle1 = Math.random() * Math.PI * 2;
      const angle2 = Math.random() * Math.PI - Math.PI / 2;
      const speed = 3 + Math.random() * 4;
      particles.push({
        mesh, timer: 2, maxTime: 2,
        vx: Math.cos(angle1) * Math.cos(angle2) * speed,
        vy: Math.sin(angle2) * speed,
        vz: Math.sin(angle1) * Math.cos(angle2) * speed,
      });
    }
  }, 1500);
}

function spawnExplosionEffect(x: number, y: number, z: number) {
  const cubeGeo = new THREE.BoxGeometry(0.2, 0.2, 0.2);
  const colors = [0x8b6914, 0x888888, 0x5b8731, 0x777777, 0xff4400, 0xffaa00]; // dirt/stone/fire colors
  for (let i = 0; i < 120; i++) {
    const color = colors[Math.floor(Math.random() * colors.length)];
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
    const mesh = new THREE.Mesh(cubeGeo, mat);
    mesh.position.set(x + (Math.random() - 0.5) * 2, y + (Math.random() - 0.5) * 2, z + (Math.random() - 0.5) * 2);
    scene.add(mesh);
    const speed = 8 + Math.random() * 16;
    const angle1 = Math.random() * Math.PI * 2;
    const angle2 = Math.random() * Math.PI * 0.8;
    particles.push({
      mesh, timer: 2, maxTime: 2,
      vx: Math.cos(angle1) * Math.sin(angle2) * speed,
      vy: Math.cos(angle2) * speed * 0.5 + 5,
      vz: Math.sin(angle1) * Math.sin(angle2) * speed,
    });
  }
}

function updateParticles(dt: number) {
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.timer -= dt;
    if (p.timer <= 0) {
      scene.remove(p.mesh);
      p.mesh.geometry.dispose();
      (p.mesh.material as THREE.MeshBasicMaterial).dispose();
      particles.splice(i, 1);
    } else {
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;
      p.vy -= 5 * dt; // gravity
      // Fade out
      const frac = p.timer / p.maxTime;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = frac * 0.9;
    }
  }
}

// --- Touch controls toggle ---
const touchToggleBtn = document.createElement('div');
touchToggleBtn.id = 'touch-toggle-btn';
touchToggleBtn.innerHTML = '&#127918;'; // gamepad emoji
touchToggleBtn.style.display = 'none';
document.body.appendChild(touchToggleBtn);

function createTouchControls() {
  if (touchControls || !player || !interaction || !hud) return;
  document.body.classList.add('game-active');
  touchControls = new TouchControls(player, {
    onBreak: () => { if (interaction.onLeftClick()) { sound.playBreak(); handSwing = 1; } },
    onPlace: () => { if (interaction.onRightClick(hud.getSelectedBlock())) { sound.playPlace(); handSwing = 1; } },
    onInventory: () => {
      if (hud.isUIOpen()) {
        hud.closeCrafting();
      } else {
        hud.toggleInventory();
      }
    },
    onChat: () => openChat(),
    onCrafting: () => {
      if (hud.isUIOpen()) {
        hud.closeCrafting();
      } else {
        hud.toggleCrafting();
      }
    },
    onHelp: () => {
      if (hud.isHelpOpen()) {
        hud.closeHelp();
      } else {
        hud.toggleHelp();
      }
    },
    onMute: () => {
      muted = !muted;
      if (muted) sound.mute(); else sound.unmute();
      touchControls?.setMuted(muted);
    },
    onPlayerList: () => {
      if (hud.isPlayerListVisible()) {
        hud.hidePlayerList();
      } else {
        const localPos = player ? player.position : { x: 0, y: 0, z: 0 };
        hud.showPlayerList(myPlayerName, localPos, player ? player.yaw : 0, remotePlayers.getPlayerList());
      }
    },
    onSelectSlot: (slot: number) => hud.selectSlot(slot),
  });
  touchToggleBtn.classList.add('active');
}

function destroyTouchControls() {
  if (!touchControls) return;
  touchControls.dispose();
  touchControls = null;
  document.body.classList.remove('game-active');
  touchToggleBtn.classList.remove('active');
}

let touchToggleHandled = false;
touchToggleBtn.addEventListener('touchstart', (e) => {
  e.preventDefault();
  e.stopPropagation();
  touchToggleHandled = true;
  if (touchControls) {
    destroyTouchControls();
  } else {
    createTouchControls();
  }
}, { passive: false });

touchToggleBtn.addEventListener('click', (e) => {
  if (touchToggleHandled) { touchToggleHandled = false; return; }
  e.preventDefault();
  e.stopPropagation();
  if (touchControls) {
    destroyTouchControls();
  } else {
    createTouchControls();
  }
});

// --- UI elements ---
const menu = document.getElementById('menu')!;
const deathScreen = document.getElementById('death-screen')!;
const oxygenBar = document.getElementById('oxygen-bar')!;
const oxygenFill = document.getElementById('oxygen-fill')!;
const waterOverlay = document.getElementById('water-overlay')!;
const timeDisplay = document.getElementById('time-display')!;
const btnNewGame = document.getElementById('btn-new-game')!;
const playerNameInput = document.getElementById('player-name') as HTMLInputElement;
const chatInput = document.getElementById('chat-input') as HTMLInputElement;
let chatOpen = false;

// --- Online player count on menu ---
const menuOnline = document.getElementById('menu-online')!;
async function fetchOnlineCount() {
  try {
    const res = await fetch('/api/players');
    const { count } = await res.json();
    if (count === 0) {
      menuOnline.innerHTML = `<span class="online-dot"></span> ${t('menu.online.none')}`;
    } else if (count === 1) {
      menuOnline.innerHTML = `<span class="online-dot"></span> ${t('menu.online.one')}`;
    } else {
      menuOnline.innerHTML = `<span class="online-dot"></span> ${t('menu.online.many', { count })}`;
    }
  } catch {
    menuOnline.textContent = '';
  }
}
fetchOnlineCount();
const onlineInterval = setInterval(fetchOnlineCount, 5000);

// --- Player identity (localStorage) ---
function getOrCreateUuid(): string {
  let uuid = localStorage.getItem('jannecraft-uuid');
  if (!uuid) {
    uuid = crypto.randomUUID();
    localStorage.setItem('jannecraft-uuid', uuid);
  }
  return uuid;
}

// Pre-fill name from localStorage
const savedName = localStorage.getItem('jannecraft-name');
if (savedName && playerNameInput) {
  playerNameInput.value = savedName;
}

// --- Character customizer ---
let currentAppearance: PlayerAppearance = (() => {
  const saved = localStorage.getItem('jannecraft-appearance');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (parsed.modelType == null) parsed.modelType = 0;
      return parsed;
    } catch {}
  }
  return defaultAppearance(0);
})();

const previewCanvas = document.getElementById('preview-canvas') as HTMLCanvasElement;
let characterPreview: CharacterPreview | null = null;

if (previewCanvas) {
  characterPreview = new CharacterPreview(previewCanvas);
  characterPreview.updateAppearance(currentAppearance);
}

function saveAppearance() {
  localStorage.setItem('jannecraft-appearance', JSON.stringify(currentAppearance));
}

function populateSwatches(
  containerId: string,
  presets: number[],
  currentColor: number,
  onSelect: (color: number) => void,
) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';
  for (const color of presets) {
    const swatch = document.createElement('div');
    swatch.className = 'swatch' + (color === currentColor ? ' selected' : '');
    swatch.style.backgroundColor = '#' + color.toString(16).padStart(6, '0');
    swatch.addEventListener('click', () => {
      container.querySelectorAll('.swatch').forEach((s) => s.classList.remove('selected'));
      swatch.classList.add('selected');
      onSelect(color);
      saveAppearance();
      if (characterPreview) characterPreview.updateAppearance(currentAppearance);
    });
    container.appendChild(swatch);
  }
}

function initSwatches() {
  populateSwatches('swatches-hair', HAIR_PRESETS, currentAppearance.hairColor, (c) => { currentAppearance.hairColor = c; });
  populateSwatches('swatches-skin', SKIN_PRESETS, currentAppearance.skinColor, (c) => { currentAppearance.skinColor = c; });
  populateSwatches('swatches-shirt', SHIRT_PRESETS, currentAppearance.shirtColor, (c) => { currentAppearance.shirtColor = c; });
  populateSwatches('swatches-pants', PANTS_PRESETS, currentAppearance.pantsColor, (c) => { currentAppearance.pantsColor = c; });
  populateSwatches('swatches-shoes', SHOE_PRESETS, currentAppearance.shoeColor, (c) => { currentAppearance.shoeColor = c; });
}

initSwatches();

// --- Model type selector ---
function initModelSelector() {
  const container = document.getElementById('model-selector');
  if (!container) return;
  container.innerHTML = '';
  for (const mt of MODEL_TYPES) {
    const btn = document.createElement('button');
    btn.className = 'model-btn' + ((currentAppearance.modelType ?? 0) === mt.id ? ' selected' : '');
    btn.textContent = getModelLabel(mt.id);
    btn.addEventListener('click', () => {
      container.querySelectorAll('.model-btn').forEach((b) => b.classList.remove('selected'));
      btn.classList.add('selected');
      currentAppearance.modelType = mt.id;
      saveAppearance();
      if (characterPreview) characterPreview.updateAppearance(currentAppearance);
    });
    container.appendChild(btn);
  }
}

initModelSelector();

// --- World Selector UI ---
import type { WorldListEntry } from './protocol';

let worldSelectorEl: HTMLElement | null = null;

function showWorldSelector(worlds: WorldListEntry[]) {
  hideWorldSelector();
  showCursor('world-selector');
  const overlay = document.createElement('div');
  overlay.id = 'world-selector';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 10000;
    background: rgba(0,0,0,0.85);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Courier New', monospace; color: #fff;
  `;

  const panel = document.createElement('div');
  panel.style.cssText = `
    background: #1a1a2e; border: 2px solid #555;
    border-radius: 8px; padding: 24px; min-width: 340px; max-width: 480px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.6);
  `;

  const title = document.createElement('h2');
  title.textContent = t('world.choose');
  title.style.cssText = 'margin: 0 0 16px 0; text-align: center; font-size: 20px; color: #8bc34a;';
  panel.appendChild(title);

  const errorDiv = document.createElement('div');
  errorDiv.id = 'world-selector-error';
  errorDiv.style.cssText = 'color: #e74c3c; text-align: center; margin-bottom: 8px; display: none; font-size: 13px;';
  panel.appendChild(errorDiv);

  const list = document.createElement('div');
  list.style.cssText = 'max-height: 300px; overflow-y: auto; margin-bottom: 16px;';

  for (const w of worlds) {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      padding: 10px 12px; margin-bottom: 6px;
      background: #16213e; border: 1px solid #333; border-radius: 4px;
      cursor: pointer; transition: background 0.15s;
    `;
    row.addEventListener('mouseenter', () => { row.style.background = '#1a3a5c'; });
    row.addEventListener('mouseleave', () => { row.style.background = '#16213e'; });

    const info = document.createElement('div');
    const nameLine = document.createElement('div');
    nameLine.style.cssText = 'font-size: 15px; font-weight: bold;';
    nameLine.textContent = w.name + (w.hasPassword ? ' \uD83D\uDD12' : '') + (w.isDefault ? ' \u2605' : '');
    info.appendChild(nameLine);

    const statsLine = document.createElement('div');
    statsLine.style.cssText = 'font-size: 12px; color: #aaa; margin-top: 2px;';
    statsLine.textContent = t('world.players', { count: w.playerCount, max: w.maxPlayers });
    info.appendChild(statsLine);

    const joinBtn = document.createElement('button');
    joinBtn.textContent = t('world.play');
    joinBtn.style.cssText = `
      background: #2ecc71; color: #000; border: none; padding: 6px 16px;
      border-radius: 4px; cursor: pointer; font-weight: bold; font-family: inherit; font-size: 13px;
    `;

    joinBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      joinWorldFromSelector(w);
    });
    row.addEventListener('click', () => joinWorldFromSelector(w));

    row.appendChild(info);
    row.appendChild(joinBtn);
    list.appendChild(row);
  }

  panel.appendChild(list);

  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  worldSelectorEl = overlay;
}

function hideWorldSelector() {
  if (worldSelectorEl) {
    worldSelectorEl.remove();
    worldSelectorEl = null;
  }
  hideCursor('world-selector');
}

function showWorldSelectorError(msg: string) {
  const el = document.getElementById('world-selector-error');
  if (el) {
    el.textContent = msg;
    el.style.display = 'block';
  }
}

function joinWorldFromSelector(w: WorldListEntry) {
  if (w.hasPassword) {
    showPasswordPrompt(w.id);
  } else {
    network.joinWorld(w.id);
  }
}

function showPasswordPrompt(worldId: string) {
  const errorDiv = document.getElementById('world-selector-error');
  if (errorDiv) errorDiv.style.display = 'none';
  showCursor('password-prompt');

  function removePrompt(el: HTMLElement) {
    el.remove();
    hideCursor('password-prompt');
  }

  // Create a simple prompt overlay on top of the world selector
  const prompt = document.createElement('div');
  prompt.id = 'password-prompt';
  prompt.style.cssText = `
    position: fixed; inset: 0; z-index: 10001;
    background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
  `;

  const box = document.createElement('div');
  box.style.cssText = `
    background: #1a1a2e; border: 2px solid #555; border-radius: 8px;
    padding: 20px; min-width: 280px;
    font-family: 'Courier New', monospace; color: #fff;
  `;

  const label = document.createElement('div');
  label.textContent = t('world.password');
  label.style.cssText = 'margin-bottom: 8px; font-size: 14px;';
  box.appendChild(label);

  const input = document.createElement('input');
  input.type = 'password';
  input.style.cssText = `
    width: 100%; box-sizing: border-box; padding: 8px; margin-bottom: 12px;
    background: #16213e; border: 1px solid #555; color: #fff; border-radius: 4px;
    font-family: inherit; font-size: 14px;
  `;
  box.appendChild(input);

  const btns = document.createElement('div');
  btns.style.cssText = 'display: flex; gap: 8px;';

  const okBtn = document.createElement('button');
  okBtn.textContent = t('world.ok');
  okBtn.style.cssText = `
    flex: 1; background: #2ecc71; color: #000; border: none; padding: 8px;
    border-radius: 4px; cursor: pointer; font-weight: bold; font-family: inherit;
  `;
  okBtn.addEventListener('click', () => {
    network.joinWorld(worldId, input.value);
    removePrompt(prompt);
  });

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = t('world.cancel');
  cancelBtn.style.cssText = `
    flex: 1; background: #555; color: #fff; border: none; padding: 8px;
    border-radius: 4px; cursor: pointer; font-family: inherit;
  `;
  cancelBtn.addEventListener('click', () => removePrompt(prompt));

  btns.appendChild(okBtn);
  btns.appendChild(cancelBtn);
  box.appendChild(btns);

  prompt.appendChild(box);
  document.body.appendChild(prompt);
  input.focus();

  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.code === 'Enter') {
      network.joinWorld(worldId, input.value);
      removePrompt(prompt);
    } else if (e.code === 'Escape') {
      removePrompt(prompt);
    }
  });
}

// --- Start game: connect to server ---
function startGame() {
  // Read and save player name
  const name = (playerNameInput?.value || '').trim() || t('menu.defaultName');
  myPlayerName = name;
  localStorage.setItem('jannecraft-name', name);
  const uuid = getOrCreateUuid();

  // Stop polling online count
  clearInterval(onlineInterval);

  // Clean up existing game
  if (player) player.dispose();
  if (world) world.dispose();
  if (interaction) interaction.dispose();
  if (remotePlayers) remotePlayers.dispose();
  if (network) network.disconnect();
  if (npcManager) {
    npcManager.dispose();
  }

  // Create world (will receive chunks from server)
  world = new World(scene);
  remotePlayers = new RemotePlayers(scene);

  // Dispose character preview
  if (characterPreview) {
    characterPreview.dispose();
    characterPreview = null;
  }

  // Create network client
  network = new NetworkClient({
    onWelcome(playerId, seed, sx, sy, sz) {
      myPlayerId = playerId;
      world.seed = seed;
      spawnX = sx;
      spawnY = sy;
      spawnZ = sz;
      remotePlayers.setLocalPlayerId(playerId);

      // Create player at spawn
      player = new Player(camera, world);
      player.position.set(spawnX, spawnY, spawnZ);

      // Wire up sound callbacks
      player.onJump = () => sound.playJump();
      player.onLand = () => sound.playLand();
      player.onStep = () => sound.playStep();
      player.onEnterWater = () => sound.playLand();
      player.onDrown = () => {
        deathScreen.classList.remove('hidden');
        showCursor('death-screen');
      };
      player.onPortalEnter = (wx, wy, wz) => {
        network.sendPortalActivate(wx, wy, wz);
      };
      player.onSpeedBoost = () => sound.playSpeedBoost();
      player.onLaunch = () => sound.playLaunch();
      player.onPaintEnter = () => sound.playPaintSplash();
      player.onTeleportBlockEnter = () => {
        const px = Math.floor(player.position.x);
        const py = Math.floor(player.position.y) - 1;
        const pz = Math.floor(player.position.z);
        network.sendTeleportBlockActivate(px, py, pz);
      };
      player.onMusicBlockStep = (wx, wy, wz) => {
        const key = `${wx},${wy},${wz}`;
        const current = musicNoteState.get(key) ?? 0;
        sound.playMusicNote(current);
        musicNoteState.set(key, (current + 1) % 8);
      };

      // Create inventory/HUD — server will send saved inventory via InventorySync
      inventory = new Inventory();
      inventory.onChange = () => {
        if (network) {
          const items = Array.from(inventory.getAll().entries())
            .map(([itemId, count]) => ({ itemId, count }));
          network.sendInventory(items);
        }
      };
      hud = new HUD(inventory);

      // Create interaction
      interaction = new Interaction(world, camera, scene, inventory);
      interaction.onBedInteract = (wx, wy, wz) => {
        network.sendBedInteract(wx, wy, wz);
      };
      interaction.onInteract = (wx, wy, wz, blockId) => {
        if (blockId === 59) {
          // Music block: cycle note and play
          const key = `${wx},${wy},${wz}`;
          const current = musicNoteState.get(key) ?? 0;
          sound.playMusicNote(current);
          musicNoteState.set(key, (current + 1) % 8);
        } else if (blockId === 62) {
          // Firework: remove block and launch effect
          world.setBlock(wx, wy, wz, 0);
          sound.playFireworkLaunch();
          spawnFireworkEffect(wx + 0.5, wy + 0.5, wz + 0.5);
        } else if (blockId === 61) {
          // TNT: send activation to server
          network.sendTNTActivate(wx, wy, wz);
        }
      };

      // Create NPC manager (receives entities from server)
      npcManager = new NPCManager(scene);

      // Create day/night cycle
      dayNight = new DayNightCycle(scene, dirLight, ambientLight, hemiLight);

      // Apply saved lighting mode
      applyLightingMode();

      // Wire world to request chunks from server
      world.onRequestChunk = (cx, cz) => {
        network.requestChunk(cx, cz);
      };

      // Wire world block modifications to send to server
      world.onBlockModify = (wx, wy, wz, blockId) => {
        dbgBlocksSent++;
        network.sendBlockModify(wx, wy, wz, blockId);
        // Immediate PointLight update for locally placed/broken blocks
        if (lightingMode === 'pointlight') {
          if (blockId === 0) {
            // Block removed — remove any light at this position
            pointLightManager.removeLight(`${wx},${wy},${wz}`);
          } else {
            const def = BLOCKS[blockId];
            if (def && def.lightLevel && def.lightLevel > 0) {
              pointLightManager.addLight(wx, wy, wz, def.lightLevel);
            }
          }
        }
      };

      gameRunning = true;
      renderer.domElement.style.visibility = 'visible';
      onlinePlayerCount = 1; // self
      updatePlayersOnline();

      // Show the touch controls toggle button during gameplay
      touchToggleBtn.style.display = '';

      if (mobile) {
        // Mobile: show touch controls instead of pointer lock
        menu.style.display = 'none';
        hideCursor('menu');
        if (!ambientStarted) {
          sound.playAmbient();
          ambientStarted = true;
        }
        createTouchControls();
      } else {
        // Request pointer lock — hide menu on success via pointerlockchange
        // If pointer lock fails (unsupported device), hide menu as fallback
        const lockPromise = document.body.requestPointerLock();
        if (lockPromise && typeof lockPromise.catch === 'function') {
          lockPromise.catch(() => {
            menu.style.display = 'none';
            hideCursor('menu');
            if (!ambientStarted) { sound.playAmbient(); ambientStarted = true; }
          });
        }
        // Timeout fallback: if pointer lock didn't fire within 500ms, hide menu anyway
        setTimeout(() => {
          if (gameRunning && menu.style.display !== 'none') {
            menu.style.display = 'none';
            hideCursor('menu');
            if (!ambientStarted) { sound.playAmbient(); ambientStarted = true; }
          }
        }, 500);
      }
    },

    onChunkData(cx, cz, blocks, maxY) {
      dbgChunksRecv++;
      world.receiveChunk(cx, cz, blocks, maxY);
    },

    onPlayerPositions(positions) {
      remotePlayers.updatePositions(positions);

      // Update network client position for sending
      if (player) {
        network.playerX = player.position.x;
        network.playerY = player.position.y;
        network.playerZ = player.position.z;
        network.playerYaw = player.yaw;
        network.playerPitch = player.pitch;
      }
    },

    onBlockUpdate(wx, wy, wz, blockId) {
      dbgBlocksRecv++;
      // Update PointLights for the old and new block
      if (lightingMode === 'pointlight') {
        const oldId = world.getBlock(wx, wy, wz);
        const oldDef = BLOCKS[oldId];
        if (oldDef && oldDef.lightLevel && oldDef.lightLevel > 0) {
          pointLightManager.removeLight(`${wx},${wy},${wz}`);
        }
      }
      // Apply block update from server (don't send back)
      world.setBlock(wx, wy, wz, blockId, false);
      if (lightingMode === 'pointlight') {
        const newDef = BLOCKS[blockId];
        if (newDef && newDef.lightLevel && newDef.lightLevel > 0) {
          pointLightManager.addLight(wx, wy, wz, newDef.lightLevel);
        }
      }
    },

    onPlayerJoin(id, name, appearance) {
      remotePlayers.addPlayer(id, name, appearance);
      onlinePlayerCount++;
      updatePlayersOnline();
      addChatMessage('Server', t('chat.playerJoined', { name }));
    },

    onPlayerLeave(id) {
      remotePlayers.removePlayer(id);
      onlinePlayerCount = Math.max(0, onlinePlayerCount - 1);
      updatePlayersOnline();
    },

    onChatMessage(name, message) {
      addChatMessage(name, message);
    },

    onTimeSync(time) {
      if (dayNight) {
        dayNight.setTime(time);
      }
    },

    onNPCSpawn(npcs) {
      if (npcManager) {
        npcManager.handleSpawn(npcs);
      }
    },

    onNPCPositions(npcs) {
      if (npcManager) {
        npcManager.handlePositions(npcs);
      }
    },

    onInventorySync(items) {
      if (inventory) {
        const data: Record<string, number> = {};
        for (const item of items) {
          data[String(item.itemId)] = item.count;
        }
        inventory.loadItems(data);
      }
    },

    onPortalTeleport(x, y, z, yaw) {
      if (player) {
        player.teleportTo(x, y, z, yaw);
        sound.playLand();
      }
    },

    onPortalSync(portals) {
      if (world) {
        world.portalData = portals;
        // Mark all chunks dirty to re-render with portal colors
        for (const [, chunk] of world.chunks) {
          chunk.dirty = true;
        }
      }
    },

    onTeleportBlockResult(x, y, z, yaw) {
      if (player) {
        player.teleportTo(x, y, z, yaw);
        sound.playTeleport();
      }
    },

    onTNTExplode(x, y, z) {
      // Clean up any fuse display at this position
      removeTNTFuse(Math.floor(x), Math.floor(y), Math.floor(z));
      sound.playExplosion();
      spawnExplosionEffect(x, y, z);
      // Camera shake
      if (player) {
        const dist = player.position.distanceTo(new THREE.Vector3(x, y, z));
        if (dist < 50) {
          cameraShakeTimer = 0.8 * Math.max(0, 1 - dist / 50);
        }
      }
    },

    onTNTFuse(wx, wy, wz, duration) {
      startTNTFuse(wx, wy, wz, duration);
      sound.playFuse();
    },

    onSleepSync(sleepingCount, totalCount, isSelfSleeping) {
      if (!player || !hud) return;
      player.sleeping = isSelfSleeping;
      if (isSelfSleeping) {
        hud.showSleepOverlay(sleepingCount, totalCount, () => {
          // Click to wake up — send another BedInteract to toggle off
          network.sendBedInteract(0, 0, 0);
        });
      } else {
        hud.hideSleepOverlay();
      }
    },

    onWorldList(worlds) {
      console.log('Received world list:', worlds.length, 'worlds');
      // If only one world with no password, auto-join
      if (worlds.length === 1 && !worlds[0].hasPassword) {
        network.joinWorld(worlds[0].id);
      } else {
        showWorldSelector(worlds);
      }
    },

    onWorldJoinResult(success, reason) {
      if (success) {
        hideWorldSelector();
        console.log('Joined world successfully');
      } else {
        console.log('Failed to join world:', reason);
        showWorldSelectorError(reason);
      }
    },

    onConnect() {
      console.log('Connected to server');
      if (netStatusEl) netStatusEl.textContent = t('net.connected');
    },

    onDisconnect() {
      console.log('Disconnected from server');
      if (netStatusEl) netStatusEl.textContent = t('net.disconnected');
      onlinePlayerCount = 0;
      updatePlayersOnline();
      if (gameRunning) {
        addChatMessage('Server', t('chat.disconnected'));
      }
    },

    onReconnecting(delayMs: number, attempt: number) {
      const seconds = Math.round(delayMs / 1000);
      if (netStatusEl) netStatusEl.textContent = t('net.connecting');
      if (gameRunning) {
        addChatMessage('Server', t('chat.reconnecting', { seconds: String(seconds), attempt: String(attempt) }));
      }
    },

    onReconnected() {
      if (gameRunning) {
        addChatMessage('Server', t('chat.reconnected'));
      }
    },
  }, name, uuid, currentAppearance);

  network.connect();
}

// --- Death screen respawn ---
deathScreen.addEventListener('click', () => {
  if (player) {
    player.respawn(spawnX, spawnY, spawnZ);
    deathScreen.classList.add('hidden');
    hideCursor('death-screen');
  }
});

// --- Menu buttons ---
btnNewGame.addEventListener('click', () => {
  startGame();
});

// Allow pressing Enter in name input to start
if (playerNameInput) {
  playerNameInput.addEventListener('keydown', (e) => {
    if (e.code === 'Enter') startGame();
  });
}

// --- Chat input ---
function openChat() {
  chatOpen = true;
  chatInput.classList.add('active');
  chatInput.value = '';
  showCursor('chat');
  if (!mobile) document.exitPointerLock();
  chatInput.focus();
}

function closeChat() {
  chatOpen = false;
  chatInput.classList.remove('active');
  chatInput.value = '';
  hideCursor('chat');
  if (!mobile) document.body.requestPointerLock();
}

chatInput.addEventListener('keydown', (e) => {
  e.stopPropagation(); // Prevent game keys (WASD etc) while typing
  if (e.code === 'Enter') {
    const text = chatInput.value.trim();
    if (text && network) {
      network.sendChatMessage(text);
    }
    closeChat();
  } else if (e.code === 'Escape') {
    closeChat();
  }
});

// --- Pointer lock ---
let ambientStarted = false;
document.addEventListener('pointerlockchange', () => {
  if (mobile) return; // Mobile doesn't use pointer lock
  if (document.pointerLockElement) {
    menu.style.display = 'none';
    hideCursor('menu');
    if (!ambientStarted) {
      sound.playAmbient();
      ambientStarted = true;
    }
  } else {
    if (gameRunning) return; // Keep game running, click to re-lock
    menu.style.display = '';
    showCursor('menu');
  }
});

// Click to re-lock pointer when game is running but pointer lock was lost
document.addEventListener('mousedown', (e) => {
  if (mobile) return;
  if (gameRunning && !document.pointerLockElement && !chatOpen && !(hud && hud.isUIOpen())) {
    document.body.requestPointerLock();
  }
});

// --- Mouse look ---
document.addEventListener('mousemove', (e) => {
  if (mobile) return;
  if (document.pointerLockElement && gameRunning && player) {
    player.onMouseMove(e.movementX, e.movementY);
  }
});

// --- Mouse clicks ---
document.addEventListener('mousedown', (e) => {
  if (mobile) return;
  if (!gameRunning || !interaction) return;
  if (hud.isUIOpen()) return;
  if (hud.isSleepOverlayVisible()) return;
  if (!document.pointerLockElement) return;
  if (e.button === 0) {
    if (interaction.onLeftClick()) { sound.playBreak(); handSwing = 1; }
  } else if (e.button === 2) {
    e.preventDefault();
    if (interaction.onRightClick(hud.getSelectedBlock())) { sound.playPlace(); handSwing = 1; }
  }
});

document.addEventListener('contextmenu', (e) => {
  e.preventDefault();
});

// --- Key handlers ---
let muted = false;
document.addEventListener('keydown', (e) => {
  if (chatOpen) return; // Chat input handles its own keys
  const hasLock = mobile || document.pointerLockElement;
  if (e.code === 'Escape' && hud && hud.isUIOpen()) {
    if (hud.isHelpOpen()) hud.closeHelp();
    else hud.closeCrafting();
    if (gameRunning && !mobile) document.body.requestPointerLock();
    return;
  }
  if ((e.code === 'KeyT' || e.code === 'Enter') && gameRunning && hasLock) {
    e.preventDefault();
    openChat();
    return;
  }
  if (e.code === 'KeyE') {
    if (hud && hud.isUIOpen()) {
      hud.closeCrafting();
      if (gameRunning && !mobile) document.body.requestPointerLock();
    } else if (gameRunning && hasLock) {
      hud.toggleInventory();
      if (!mobile) document.exitPointerLock();
    }
  }
  if (e.code === 'KeyC') {
    if (hud && hud.isUIOpen()) {
      hud.closeCrafting();
      if (gameRunning && !mobile) document.body.requestPointerLock();
    } else if (gameRunning && hasLock) {
      hud.toggleCrafting();
      if (!mobile) document.exitPointerLock();
    }
  }
  if (e.code === 'KeyH') {
    if (hud && hud.isHelpOpen()) {
      hud.closeHelp();
      if (gameRunning && !mobile) document.body.requestPointerLock();
    } else if (gameRunning && hasLock) {
      hud.toggleHelp();
      if (!mobile) document.exitPointerLock();
    }
  }
  if (e.code === 'KeyM') {
    muted = !muted;
    if (muted) sound.mute(); else sound.unmute();
    touchControls?.setMuted(muted);
  }
  if (e.code === 'KeyL' && gameRunning && !hud.isUIOpen()) {
    lightingMode = lightingMode === 'lightmap' ? 'pointlight' : 'lightmap';
    applyLightingMode();
    const label = lightingMode === 'lightmap' ? 'Lightmap' : 'PointLight';
    addChatMessage('System', `Verlichting: ${label}`);
  }
  if (e.code === 'Tab' && gameRunning && !hud.isUIOpen()) {
    e.preventDefault();
    const localPos = player ? player.position : { x: 0, y: 0, z: 0 };
    hud.showPlayerList(myPlayerName, localPos, player ? player.yaw : 0, remotePlayers.getPlayerList());
  }
  if (e.code >= 'Digit1' && e.code <= 'Digit9') {
    const slot = parseInt(e.code.replace('Digit', ''), 10) - 1;
    hud.selectSlot(slot);
  }
});

document.addEventListener('keyup', (e) => {
  if (e.code === 'Tab') {
    hud.hidePlayerList();
  }
});

// --- Window resize ---
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  handCamera.aspect = window.innerWidth / window.innerHeight;
  handCamera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

// --- Debug counters ---
let dbgBlocksSent = 0;
let dbgBlocksRecv = 0;
let dbgChunksRecv = 0;

// --- Chat (server messages only, no NPC random chat) ---
const chatDiv = document.getElementById('chat')!;
let chatMessages: { text: string; time: number }[] = [];
const netStatusEl = document.getElementById('net-status');
const playersOnlineEl = document.getElementById('players-online');
let onlinePlayerCount = 0;

function updatePlayersOnline() {
  if (playersOnlineEl) {
    playersOnlineEl.textContent = t('hud.players', { count: onlinePlayerCount });
  }
}

function addChatMessage(sender: string, message: string) {
  const text = `<b>${escapeHtml(sender)}</b>: ${escapeHtml(message)}`;
  chatMessages.push({ text, time: performance.now() });

  if (chatMessages.length > 50) {
    chatMessages = chatMessages.slice(-50);
  }

  renderChat();
  chatDiv.scrollTop = chatDiv.scrollHeight;
}

function renderChat() {
  chatDiv.innerHTML = chatMessages
    .map((m) => `<div class="chat-msg">${m.text}</div>`)
    .join('');
}

function updateChat() {
  // no-op: messages persist
}

function updateNetStatus() {
  if (!netStatusEl) return;
  if (!network) {
    netStatusEl.textContent = t('net.notConnected');
    return;
  }
  const connected = network.connected;
  const chunks = world ? world.chunks.size : 0;
  const id = myPlayerId >= 0 ? `#${myPlayerId}` : '-';
  netStatusEl.textContent = connected
    ? t('net.status', { id, chunks, sent: dbgBlocksSent, recv: dbgBlocksRecv })
    : t('net.disconnected');
}

// --- Game loop ---
const clock = new THREE.Clock();
let prevSkyBrightness = 1.0;

function gameLoop() {
  requestAnimationFrame(gameLoop);

  const dt = clock.getDelta();

  if (!gameRunning || !player) {
    renderer.render(scene, camera);
    return;
  }

  // Update player position for network sending
  if (network) {
    network.playerX = player.position.x;
    network.playerY = player.position.y;
    network.playerZ = player.position.z;
    network.playerYaw = player.yaw;
    network.playerPitch = player.pitch;
  }

  // Update systems
  player.update(dt);
  dayNight.update(dt);

  // Update lightmap sky brightness and mark chunks dirty on day/night transitions
  const currentSkyBri = dayNight.getSkyBrightness();
  world.skyBrightness = currentSkyBri;
  if (Math.abs(currentSkyBri - prevSkyBrightness) > 0.02) {
    prevSkyBrightness = currentSkyBri;
    for (const [, ch] of world.chunks) {
      ch.dirty = true;
    }
  }

  world.update(player.position.x, player.position.z, 6);
  interaction.playerYaw = player.yaw;
  interaction.update();
  npcManager.update(dt);
  remotePlayers.update(dt);
  hud.update(player, dt);

  // Update particle systems
  updateParticles(dt);
  updateFootprints(dt);
  updateTNTFuses(dt);

  // Update PointLight positions periodically
  if (lightingMode === 'pointlight') {
    lightRefreshCounter++;
    if (lightRefreshCounter >= LIGHT_REFRESH_INTERVAL) {
      lightRefreshCounter = 0;
      pointLightManager.refresh(player.position);
    }
  }

  // Paint footprints
  if (player.paintColor > 0 && player.onGround && (player.velocity.x !== 0 || player.velocity.z !== 0)) {
    footprintTimer += dt;
    if (footprintTimer >= 0.5) {
      footprintTimer = 0;
      const groundY = Math.floor(player.position.y);
      addFootprint(player.position.x, groundY, player.position.z, player.paintColor);
    }
  } else {
    footprintTimer = 0;
  }

  // Camera shake
  if (cameraShakeTimer > 0) {
    cameraShakeTimer -= dt;
    const intensity = cameraShakeTimer * 0.3;
    camera.position.x += (Math.random() - 0.5) * intensity;
    camera.position.y += (Math.random() - 0.5) * intensity;
    camera.position.z += (Math.random() - 0.5) * intensity;
  }

  // Water overlay & oxygen bar
  if (player.inWater) {
    waterOverlay.classList.add('active');
    oxygenBar.classList.remove('hidden');
    oxygenFill.style.width = `${player.oxygen}%`;
    if (player.oxygen < 30) {
      oxygenFill.classList.add('low');
    } else {
      oxygenFill.classList.remove('low');
    }
  } else {
    waterOverlay.classList.remove('active');
    if (player.oxygen >= 100) {
      oxygenBar.classList.add('hidden');
    } else {
      oxygenFill.style.width = `${player.oxygen}%`;
    }
  }

  // Time display
  timeDisplay.textContent = dayNight.getTimeString();

  // Chat + network status
  updateChat();
  updateNetStatus();

  // Update held item — only show if player actually has it in inventory
  const selectedId = hud.getSelectedBlock();
  updateHandMesh(inventory.has(selectedId) ? selectedId : 0);

  // Update held torch light position
  if (torchLight) {
    torchLight.position.copy(camera.position);
  }

  // Animate hand swing
  if (handSwing > 0) {
    handSwing = Math.max(0, handSwing - dt * 6);
  }

  // Render
  renderer.render(scene, camera);
  renderer.autoClear = false;
  renderer.clearDepth();
  renderer.render(handScene, handCamera);
  renderer.autoClear = true;
}

// Re-render dynamic text on language change
window.addEventListener('languagechange', () => {
  initModelSelector();
  fetchOnlineCount();
  updatePlayersOnline();
});

gameLoop();
